import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, AlertTriangle, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';

interface DiagnosticVideo {
  id: string;
  title: string | null;
  mux_asset_id: string | null;
  mux_playback_id: string | null;
  playback_policy: string | null;
  status: string | null;
  created_at: string;
}

export default function DiagnosticPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState<DiagnosticVideo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [fixing, setFixing] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    diagnoseVideos();
  }, []);

  const diagnoseVideos = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError('Please sign in to run diagnostics');
        setLoading(false);
        return;
      }

      // Find videos with missing IDs
      const { data, error: fetchError } = await supabase
        .from('videos')
        .select('id, title, mux_asset_id, mux_playback_id, playback_policy, status, created_at')
        .eq('user_id', user.id)
        .or('mux_asset_id.is.null,mux_playback_id.is.null')
        .order('created_at', { ascending: false });

      if (fetchError) {
        setError(`Error fetching videos: ${fetchError.message}`);
      } else {
        setVideos(data || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const deleteVideo = async (videoId: string) => {
    if (!confirm('Are you sure you want to delete this video record?')) return;
    
    setFixing(true);
    try {
      const { error } = await supabase
        .from('videos')
        .delete()
        .eq('id', videoId);

      if (error) {
        alert(`Error deleting video: ${error.message}`);
      } else {
        // Refresh the list
        await diagnoseVideos();
      }
    } finally {
      setFixing(false);
    }
  };

  const syncMuxStatus = async () => {
    // Mux pipeline has been retired - this function is no longer available
    toast({
      variant: "destructive", 
      title: "Function Retired",
      description: "Mux sync functionality has been retired from the platform"
    });
  };

  const categorizeVideos = () => {
    const bothMissing = videos.filter(v => !v.mux_asset_id && !v.mux_playback_id);
    const assetMissing = videos.filter(v => !v.mux_asset_id && v.mux_playback_id);
    const playbackMissing = videos.filter(v => v.mux_asset_id && !v.mux_playback_id);
    
    return { bothMissing, assetMissing, playbackMissing };
  };

  const { bothMissing, assetMissing, playbackMissing } = categorizeVideos();

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 flex items-center gap-4">
          <Button 
            variant="outline" 
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Button>
          <h1 className="text-3xl font-bold">Video Diagnostics</h1>
        </div>

        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-2 text-gray-600">Running diagnostics...</p>
          </div>
        )}

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!loading && !error && (
          <>
            {videos.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">All Clear!</h3>
                  <p className="text-gray-600">No videos found with missing IDs.</p>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Both IDs Missing</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-red-600">{bothMissing.length}</div>
                      <p className="text-sm text-gray-600">Failed uploads</p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Asset ID Missing</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-yellow-600">{assetMissing.length}</div>
                      <p className="text-sm text-gray-600">Unusual case</p>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg">Playback ID Missing</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-yellow-600">{playbackMissing.length}</div>
                      <p className="text-sm text-gray-600">Can be synced</p>
                    </CardContent>
                  </Card>
                </div>

                {bothMissing.length > 0 && (
                  <Card className="mb-6">
                    <CardHeader>
                      <CardTitle className="text-xl flex items-center gap-2">
                        <XCircle className="h-5 w-5 text-red-600" />
                        Critical: Videos Missing Both IDs
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-4">
                        These videos have neither asset ID nor playback ID, indicating failed uploads or incomplete records.
                      </p>
                      <div className="space-y-2">
                        {bothMissing.map(video => (
                          <div key={video.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                            <div>
                              <div className="font-medium">{video.title || 'Untitled Video'}</div>
                              <div className="text-sm text-gray-600">
                                ID: {video.id.slice(0, 8)}... | Status: {video.status || 'unknown'}
                              </div>
                              <div className="text-xs text-gray-500">
                                Created: {new Date(video.created_at).toLocaleDateString()}
                              </div>
                            </div>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => deleteVideo(video.id)}
                              disabled={fixing}
                            >
                              Delete
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {assetMissing.length > 0 && (
                  <Card className="mb-6">
                    <CardHeader>
                      <CardTitle className="text-xl flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-yellow-600" />
                        Videos Missing Asset ID Only
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-4">
                        Unusual case where playback ID exists but asset ID is missing.
                      </p>
                      <div className="space-y-2">
                        {assetMissing.map(video => (
                          <div key={video.id} className="p-3 bg-yellow-50 rounded-lg">
                            <div className="font-medium">{video.title || 'Untitled Video'}</div>
                            <div className="text-sm text-gray-600">
                              Playback ID: {video.mux_playback_id}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {playbackMissing.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-xl flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-yellow-600" />
                        Videos Missing Playback ID Only
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-4">
                        These can potentially be fixed by syncing with Mux API.
                      </p>
                      <div className="space-y-2">
                        {playbackMissing.map(video => (
                          <div key={video.id} className="p-3 bg-yellow-50 rounded-lg">
                            <div className="font-medium">{video.title || 'Untitled Video'}</div>
                            <div className="text-sm text-gray-600">
                              Asset ID: {video.mux_asset_id}
                            </div>
                          </div>
                        ))}
                      </div>
                      <Button 
                        className="mt-4"
                        onClick={() => alert('Run the sync-mux-playback-ids.py script to fix these videos')}
                      >
                        How to Fix
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            <div className="mt-6 flex justify-center gap-4">
              <Button onClick={diagnoseVideos} disabled={loading}>
                Re-run Diagnostics
              </Button>
              <Button 
                onClick={syncMuxStatus} 
                disabled={syncing}
                variant="outline"
                className="flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                Sync Video Status
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
